import {Card, CardBody} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, useNavigate} from '@tanstack/react-router'
import React from "react";
import {apiActionImproved} from "../../../api.ts";
import Content from "../../../components/Content.tsx";
import PButton from "../../../components/PButton.tsx";
import Header from "../../../Header.tsx";
import services from "../../../services";
import queryKeys from "../../../utilities/queryKeys.ts";

export const Route = createFileRoute('/_app/running/dashboard')({
    component: RouteComponent,
})

function Stat({label, value, icon, tone = 'default'}: {label: string; value: React.ReactNode; icon: string; tone?: string}) {
    return (
        <div className="rounded-xl border border-default-200 bg-content1 p-4">
            <div className="flex items-center gap-2 text-default-400">
                <Icon icon={icon} className="text-xl" />
                <span className="text-xs uppercase tracking-wide">{label}</span>
            </div>
            <div className={`mt-1 text-2xl font-bold ${tone === 'primary' ? 'text-primary' : ''}`}>{value}</div>
        </div>
    );
}

function RouteComponent() {
    const navigate = useNavigate();

    const queryClient = useQueryClient();

    const dash = useQuery({
        queryKey: ['races-dashboard'],
        queryFn:  () => services.races.dashboard(),
    });

    // Season leaderboards live here rather than behind a dropdown — they were previously only
    // reachable from a menu that looked empty until you'd already created one.
    const leaderboards = useQuery({
        queryKey: queryKeys.running.getRankings(),
        queryFn:  () => services.running.getRankings(),
    });

    const createLeaderboard = async () => {
        const name = window.prompt('Name this season leaderboard', 'Season 2026');
        if (!name) return;
        try {
            await apiActionImproved('ranking-groups-create' as any, {sport_type: 'running', name});
            queryClient.invalidateQueries({queryKey: queryKeys.running.getRankings()});
            addToast({color: 'success', title: 'Leaderboard created — now set its points.'});
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || 'Could not create the leaderboard.'});
        }
    };

    const t = dash.data?.totals;
    const upcoming = dash.data?.upcoming || [];
    const recent = dash.data?.recent_races || [];

    return <>
        <Header backUrl={"/running"} />
        <Content>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h1 className="text-2xl font-bold">Running dashboard</h1>
                <div className="flex gap-2">
                    <PButton onPress={() => navigate({to: '/running/races/'})}
                             startContent={<Icon icon="material-symbols-light:flag-outline" className="text-xl" />}>
                        Races
                    </PButton>
                    <PButton color="primary" onPress={() => navigate({to: '/running/upload/'})}
                             startContent={<Icon icon="material-symbols-light:upload" className="text-xl" />}>
                        Upload results
                    </PButton>
                </div>
            </div>

            {dash.isLoading ? (
                <div className="flex justify-center py-12"><Spinner size="lg" /></div>
            ) : (<>
                <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
                    <Stat label="Races" value={t?.races ?? 0} icon="material-symbols-light:flag-outline" />
                    <Stat label="Published" value={t?.published ?? 0} icon="material-symbols-light:public" />
                    <Stat label="Completed" value={t?.completed ?? 0} icon="material-symbols-light:check-circle-outline" />
                    <Stat label="Entries" value={t?.entries ?? 0} icon="material-symbols-light:groups-outline" tone="primary" />
                    <Stat label="Finishers" value={t?.finishers ?? 0} icon="material-symbols-light:trophy-outline" />
                    <Stat label="Open for entry" value={t?.open_for_entry ?? 0} icon="material-symbols-light:how-to-reg-outline" />
                </div>

                <Card className="mb-4">
                    <CardBody>
                        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                            <h2 className="font-bold">Season leaderboards</h2>
                            <PButton size="sm" color="primary" onPress={createLeaderboard}
                                     startContent={<Icon icon="material-symbols-light:add" className="text-lg" />}>
                                New leaderboard
                            </PButton>
                        </div>
                        <p className="mb-3 text-sm text-default-500">
                            A season table that adds up points across several races. Create one, set its points
                            per finishing position, then attach races to it from the Races page.
                        </p>

                        {leaderboards.isLoading ? (
                            <div className="flex justify-center py-4"><Spinner size="sm" /></div>
                        ) : (leaderboards.data || []).length === 0 ? (
                            <div className="rounded-xl border border-dashed border-default-300 py-6 text-center text-sm text-default-500">
                                No leaderboards yet — create one to start a season table.
                            </div>
                        ) : (leaderboards.data || []).map((lb: any) => (
                            <div key={lb.uid} className="flex flex-wrap items-center justify-between gap-2 border-b border-default-100 py-2 last:border-0">
                                <div className="min-w-0">
                                    <div className="truncate font-medium">{lb.name}</div>
                                    <div className="text-xs text-default-400">
                                        {lb.events_count > 0
                                            ? `${lb.events_count} race${lb.events_count > 1 ? 's' : ''} counting`
                                            : 'No races attached yet — nothing will show'}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <PButton size="sm" variant="bordered"
                                             onPress={() => navigate({to: `/running/leaderboards/${lb.uid}`})}>
                                        Points
                                    </PButton>
                                    <PButton size="sm" color="primary"
                                             onPress={() => navigate({to: `/running/leaderboards-results/${lb.uid}`})}>
                                        View table
                                    </PButton>
                                </div>
                            </div>
                        ))}
                    </CardBody>
                </Card>

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <Card>
                        <CardBody>
                            <h2 className="mb-3 font-bold">Coming up</h2>
                            {upcoming.length === 0 ? (
                                <p className="text-sm text-default-500">No races scheduled yet.</p>
                            ) : upcoming.map((r: any) => (
                                <button key={r.uid} onClick={() => navigate({to: `/running/races/${r.uid}`})}
                                        className="flex w-full items-center justify-between border-b border-default-100 py-2 text-left last:border-0 hover:bg-default-50">
                                    <span>
                                        <span className="font-medium">{r.title}</span>
                                        <span className="block text-xs text-default-400">
                                            {r.starts_at ? new Date(r.starts_at).toLocaleString() : 'Date TBC'}
                                        </span>
                                    </span>
                                    <span className="text-sm text-default-500">{r.entries_count} entered</span>
                                </button>
                            ))}
                        </CardBody>
                    </Card>

                    <Card>
                        <CardBody>
                            <h2 className="mb-3 font-bold">Recent races</h2>
                            {recent.length === 0 ? (
                                <p className="text-sm text-default-500">Nothing yet — create a race or upload results.</p>
                            ) : recent.map((r: any) => (
                                <button key={r.uid} onClick={() => navigate({to: `/running/races/${r.uid}`})}
                                        className="flex w-full items-center justify-between border-b border-default-100 py-2 text-left last:border-0 hover:bg-default-50">
                                    <span>
                                        <span className="font-medium">{r.title}</span>
                                        <span className="block text-xs text-default-400 capitalize">{r.status}</span>
                                    </span>
                                    <span className="text-sm text-default-500">
                                        {r.finished > 0 ? `${r.finished} finished` : `${r.entries_count} entered`}
                                    </span>
                                </button>
                            ))}
                        </CardBody>
                    </Card>
                </div>
            </>)}
        </Content>
    </>
}
